import { Component, h, Prop, State, Watch } from "@stencil/core"
import { PaymentMethodEnum } from "../../enums/PaymentMethodEnum"
import { validatePaymentMethodEnum } from "../../helpers/Validators"
import { HorizontalMarkSizeEnum, HorizontalMarkSizeEnumsObj } from "../../enums/HorizontalMarkSizeEnum"
import { InfoMessageModel, MessageMaxLength } from "../infoMessage/InfoMessageModel"
import { srcScreenError } from "../../helpers/CommonRenders"
import LanguageHandler from "@payair/e-com-common-sdk/core/LanguageHandler"
import { getSupportedLocaleData, getLanguageHandler, getLocale } from "../../languageHandler/LanguageHandler"

/**
 * Entire screen to handle OTP.
 */
@Component({
  tag: "otp-screen",
  styleUrl: "OtpScreen.scss",
})
export class OtpScreen {
  /**
   * Merchant site preferred locale. Format based on ISO format for language (ISO 639-1) and country code
   * (ISO 3166-1 alpha-2). The language and country should be separated using an underscore ( _ ).
   * <br /><br />
   * **Supported locale**: en_GB, sv_SE, pl_PL
   * <br /><br />
   * @default en_GB
   */
  @Prop() locale?: string

  /**
   * Indicator which Click to Pay System is supported.
   */
  @Prop() paymentMethod: PaymentMethodEnum

  /**
   * Indicator if the screen is loading.
   *
   * @default false
   */
  @Prop() isLoading?: boolean

  /**
   * Current Remember Me choice.
   *
   * @default false
   */
  @Prop() rememberMe?: boolean

  /**
   * Callback called after Remember Me checkbox change.
   *
   * @param rememberMe Current Remember Me choice.
   */
  @Prop() rememberMeOnChange?: (rememberMe: boolean) => void

  /**
   * A place for a major error. It is displayed as a InfoMessage component.
   */
  @Prop() majorError?: InfoMessageModel | undefined

  /**
   * A place for minor error. Is is displayed below OTP input.
   */
  @Prop() minorError?: string

  /**
   * A info place for a value about OTP validation channel. Email address of a phone number.
   */
  @Prop() maskedValidationChannel?: string

  /**
   * A indicator to display "Continue another way" or "Resend" text in the resend button.
   */
  @Prop() isChangeChannel: boolean = false

  /**
   * Callback called after close button click.
   */
  @Prop() closeButtonOnClick?: () => void

  /**
   * Callback called after continue button click
   *
   * @param otp Value with OTP value passed by the customer.
   */
  @Prop() continueButtonOnClick: (otp: string | null) => void

  /**
   * Callback called after resend button click.
   */
  @Prop() resendButtonOnClick: () => void

  /**
   * Callback called after change user button click.
   */
  @Prop() changeUserButtonOnClick?: () => void

  @State() horizontalMarkSize: HorizontalMarkSizeEnum
  @State() otpValue: string | null
  @State() isLoadingState: boolean = this.isLoading === true ? true : false

  private languageHandler: LanguageHandler
  private errorMessage: string[]
  private supportedLocale: string

  componentWillLoad() {
    this.errorMessage = []

    if (!this.paymentMethod) {
      this.errorMessage.push("'paymentMethod' prop is not defined")
    } else if (!validatePaymentMethodEnum(this.paymentMethod)) {
      this.errorMessage.push("'paymentMethod' prop is not valid")
    }

    if (this.errorMessage.length > 0) {
      return
    }

    this.horizontalMarkSize = HorizontalMarkSizeEnumsObj.S16

    const localeData = getSupportedLocaleData(this.locale)
    this.languageHandler = getLanguageHandler(localeData.lang)
    this.supportedLocale = getLocale(localeData)

    if (this.majorError && this.majorError.infoMessage.length > MessageMaxLength.OTP_SCREN) {
      this.majorError.infoMessage = this.majorError.infoMessage.substring(0, MessageMaxLength.OTP_SCREN)
    }

    if (this.minorError && this.minorError.length > MessageMaxLength.OTP_SCREN) {
      this.minorError = this.minorError.substring(0, MessageMaxLength.OTP_SCREN)
    }
  }

  otpOnInput = (value: string | null) => {
    this.otpValue = value
  }

  rememberMeCheckboxOnChange = (isChecked: boolean) => {
    this.rememberMeOnChange?.(isChecked)
  }

  onContinueButtonClick = () => {
    if (this.otpValue?.length === 6) {
      this.continueButtonOnClick(this.otpValue)
    } else {
      this.minorError = this.languageHandler.getString("otpTooShortCode")
    }
  }

  @Watch("isLoading")
  updateIsLoadingState() {
    this.isLoadingState = this.isLoading === true ? true : false
  }

  render() {
    if (this.errorMessage.length > 0) {
      return srcScreenError("OTP Screen", this.errorMessage)
    }

    return (
      <div class="otpScreen">
        <div class="closeButtonContainer">
          {typeof this.closeButtonOnClick === "function" && (
            <close-button closeButtonOnClick={this.closeButtonOnClick}></close-button>
          )}
        </div>
        <div class="otp">
          <label class="otpHeaderMargin">{this.languageHandler.getString("welcomeBackToClickToPay")}</label>
          <horizontal-mark
            class="srcMark"
            locale={this.supportedLocale}
            paymentMethod={this.paymentMethod}
            horizontalMarkSize={this.horizontalMarkSize}></horizontal-mark>

          {this.majorError && Boolean(this.majorError.infoMessage) && <info-message infoMessage={this.majorError} />}
          <label class="otpSubtitle">
            {this.maskedValidationChannel && this.languageHandler.getString("existingAccount")}
          </label>
          <label class="otpText">
            <switch-user
              locale={this.supportedLocale}
              switchUserText={
                !this.maskedValidationChannel ? this.languageHandler.getString("createAccount") : undefined
              }
              userCredentials={
                this.maskedValidationChannel
                  ? this.languageHandler.getString("otpInformation") + this.maskedValidationChannel
                  : undefined
              }
              changeUserButtonOnClick={this.changeUserButtonOnClick}
            />
          </label>
          <otp-input
            otpOnInput={this.otpOnInput}
            otpOnEnterClick={this.onContinueButtonClick}
            errorMessage={this.minorError}></otp-input>
          <div class="otpRememberMeSection">
            <remember-me-control
              locale={this.supportedLocale}
              checked={this.rememberMe}
              disabled={this.isLoadingState}
              rememberMeOnChange={this.rememberMeCheckboxOnChange}
            />
          </div>
        </div>
        <div class="otpButtonDiv">
          {this.maskedValidationChannel && (
            <button class="resendButton" disabled={this.isLoading} onClick={this.resendButtonOnClick}>
              {this.isChangeChannel
                ? this.languageHandler.getString("changeChannel")
                : this.languageHandler.getString("resend")}
            </button>
          )}
          <button class="otpContinueButton" onClick={this.onContinueButtonClick} disabled={this.isLoading}>
            {this.isLoadingState === false ? (
              <div>{this.languageHandler.getString("continue")}</div>
            ) : (
              <loading-spinner loadingText={this.languageHandler.getString("processing")} />
            )}
          </button>
        </div>
      </div>
    )
  }
}
